import { useEffect, useRef, useState } from 'react'
import { ChevronUp, ChevronDown, X, StickyNote, AlertCircle, Target } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { CitationChip } from './CitationChip'
import type { EditableSection } from '@/stores/outline-proposal-store'

interface ProposedSectionRowProps {
  section: EditableSection
  /** 1-based number shown on the row. In additions mode it continues the existing outline. */
  displayNumber: number
  isFirst: boolean
  isLast: boolean
  criteriaNames: Map<string, { title: string; weight: number | null }>
  themeNames: Map<string, string>
  /**
   * Additions mode: tints the row and badges it "New" so it can never be
   * mistaken for a section the document already has.
   */
  isAddition?: boolean
  /**
   * Same-title collision (spec §2-C3). Rendered on the row **before** accept —
   * a post-hoc toast is too late, because the appended section inherits the
   * older one's draft status and word count the moment it lands.
   */
  warning?: string | null
  /**
   * Focus the title on mount. Set for a row the reviewer just added by hand —
   * it arrives blank, and an empty borderless input is otherwise invisible.
   */
  autoFocusTitle?: boolean
  onUpdate: (patch: Partial<Pick<EditableSection, 'title' | 'suggestedWordCount' | 'userNotes'>>) => void
  onRemove: () => void
  onMove: (direction: -1 | 1) => void
}

export function ProposedSectionRow({
  section,
  displayNumber,
  isFirst,
  isLast,
  criteriaNames,
  themeNames,
  isAddition = false,
  warning = null,
  autoFocusTitle = false,
  onUpdate,
  onRemove,
  onMove,
}: ProposedSectionRowProps) {
  const [showNotes, setShowNotes] = useState(section.userNotes.length > 0)
  const titleRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocusTitle) titleRef.current?.focus()
  }, [autoFocusTitle])

  return (
    <div
      className={cn(
        'border-t border-border/60 px-4 py-3 first:border-t-0',
        // Additions mode marks each proposed row with a 2px iris left edge, not
        // an `--ai-accent-soft` fill. The fill repeats once per row, so a
        // twelve-section proposal became twelve stacked bands of an
        // 85%-saturation token — and the "New" badge below already says the same
        // thing more precisely.
        isAddition && 'border-l-2 border-l-[hsl(var(--ai-accent))]'
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-1.5 w-5 shrink-0 text-meta">{displayNumber}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isAddition && (
              <Badge
                variant="outline"
                className="shrink-0 border-[hsl(var(--ai-accent)/0.25)] bg-card text-[hsl(var(--ai-accent-text))]"
              >
                New
              </Badge>
            )}
            <Input
              ref={titleRef}
              value={section.title}
              onChange={(e) => onUpdate({ title: e.target.value })}
              placeholder="Section title"
              className="h-7 border-transparent bg-transparent px-1 text-sm font-medium hover:border-border focus-visible:border-border"
              aria-label="Section title"
            />
            {section.suggestedWordCount != null && (
              <div className="flex shrink-0 items-center gap-1 text-meta">
                <Input
                  type="number"
                  value={section.suggestedWordCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10)
                    onUpdate({ suggestedWordCount: Number.isNaN(value) ? null : value })
                  }}
                  className="h-7 w-20 px-1 text-right text-xs"
                  aria-label="Word count"
                />
                words
              </div>
            )}
          </div>

          {warning && (
            <div
              role="alert"
              className="mt-1.5 flex items-start gap-2 rounded-lg bg-[hsl(var(--status-warning)/0.12)] px-2 py-1.5 text-xs text-[hsl(var(--status-warning))]"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{warning}</span>
            </div>
          )}

          {section.guidance && (
            <p className="mt-1 px-1 text-xs leading-relaxed text-muted-foreground">
              {section.guidance}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 px-1">
            {section.mappedCriteriaIds.map((id) => {
              const criterion = criteriaNames.get(id)
              if (!criterion) return null
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/40 px-1.5 py-0.5 text-meta"
                >
                  <Target className="h-3 w-3 shrink-0 text-subtle" />
                  {criterion.title}
                  {criterion.weight != null && ` · ${criterion.weight}%`}
                </span>
              )
            })}
            {section.themeIds.map((id) => {
              const title = themeNames.get(id)
              if (!title) return null
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/40 px-1.5 py-0.5 text-meta"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))] opacity-60" />
                  {title}
                </span>
              )
            })}
            {section.citations.map((citation, i) => (
              <CitationChip key={i} citation={citation} />
            ))}
            {!showNotes && (
              <button
                type="button"
                onClick={() => setShowNotes(true)}
                className="inline-flex items-center gap-1 rounded-lg border border-dashed border-border px-1.5 py-0.5 text-meta hover:bg-muted"
              >
                <StickyNote className="h-3 w-3" />
                Add note
              </button>
            )}
          </div>

          {showNotes && (
            <div className="mt-2 px-1">
              <Textarea
                value={section.userNotes}
                onChange={(e) => onUpdate({ userNotes: e.target.value })}
                placeholder="Your notes for this section — points to hit, evidence to lean on, things to avoid. These guide the AI when drafting."
                className="min-h-[56px] border-l-2 border-l-[hsl(var(--brand))] text-sm"
                aria-label="Section notes"
              />
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={isFirst}
            onClick={() => onMove(-1)}
            title="Move up"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            disabled={isLast}
            onClick={() => onMove(1)}
            title="Move down"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:text-destructive"
            onClick={onRemove}
            title="Remove section"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
